import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/authContext';
import { getNotifications, markNotificationAsRead, markAllNotificationsAsRead, deleteNotification } from '../services/api';
import { Heading, Text, Modal } from '../components/ui';
import RecipeDiffModal from '../components/RecipeDiffModal';
import FriendRequestModal from '../components/FriendRequestModal';
import { getRecipeById, getMergeProposal, updateMergeProposal } from '../services/api';

const colorTokens = {
    alabaster: '#EEEEE7',
    white: '#FFFFFF',
    oxfordBlue: '#1b263b',
    richBlack: '#0d1b2a',
    neutral: {
        0: '#FFFFFF',
        50: '#F9F8F6',
        900: '#0D0D0D'
    }
};

const NotificationsPage = () => {
    const { user } = useAuth();
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [filter, setFilter] = useState('all'); // 'all', 'unread', 'read'
    const [selectedProposal, setSelectedProposal] = useState(null);
    const [originalRecipe, setOriginalRecipe] = useState(null);
    const [updatedRecipe, setUpdatedRecipe] = useState(null);
    const [showDiffModal, setShowDiffModal] = useState(false);
    const [processingMerge, setProcessingMerge] = useState(false);
    const [showFriendRequestModal, setShowFriendRequestModal] = useState(false);
    const [selectedFriendRequest, setSelectedFriendRequest] = useState(null);
    const [showNotificationModal, setShowNotificationModal] = useState(false);
    const [selectedNotification, setSelectedNotification] = useState(null);

    useEffect(() => {
        if (user) {
            fetchNotifications();
        }
    }, [user, filter]);

    const fetchNotifications = async () => {
        if (!user) return;
        
        setLoading(true);
        setError(null);
        
        try {
            const token = await user.getIdToken();
            const options = { limit: 100 };
            if (filter === 'unread') {
                options.unread_only = true;
            }
            
            const data = await getNotifications(options, token);
            setNotifications(data.notifications);
            setUnreadCount(data.unread_count);
        } catch (err) {
            console.error('Error fetching notifications:', err);
            setError('Failed to load notifications');
        } finally {
            setLoading(false);
        }
    };

    const handleMarkAsRead = async (notificationId) => {
        if (!user) return;
        
        try {
            const token = await user.getIdToken();
            await markNotificationAsRead(notificationId, token);
            
            // Update local state
            setNotifications(prev => 
                prev.map(n => 
                    n.id === notificationId 
                        ? { ...n, is_read: true, read_at: new Date().toISOString() }
                        : n
                )
            );
            setUnreadCount(prev => Math.max(0, prev - 1));
        } catch (err) {
            console.error('Error marking notification as read:', err);
        }
    };

    const handleMarkAllAsRead = async () => {
        if (!user) return;
        
        try {
            const token = await user.getIdToken();
            await markAllNotificationsAsRead(token);
            
            // Update local state
            setNotifications(prev => 
                prev.map(n => ({ ...n, is_read: true, read_at: new Date().toISOString() }))
            );
            setUnreadCount(0);
        } catch (err) {
            console.error('Error marking all notifications as read:', err);
        }
    };

    const handleDelete = async (notificationId) => {
        if (!user) return;
        
        try {
            const token = await user.getIdToken();
            await deleteNotification(notificationId, token);
            
            // Update local state
            setNotifications(prev => prev.filter(n => n.id !== notificationId));
            setUnreadCount(prev => Math.max(0, prev - 1));
        } catch (err) {
            console.error('Error deleting notification:', err);
        }
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        const now = new Date();
        const diffInHours = (now - date) / (1000 * 60 * 60);
        
        if (diffInHours < 1) {
            return 'Just now';
        } else if (diffInHours < 24) {
            return `${Math.floor(diffInHours)}h ago`;
        } else if (diffInHours < 168) { // 7 days
            return `${Math.floor(diffInHours / 24)}d ago`;
        } else {
            return date.toLocaleDateString();
        }
    };

    const getNotificationIcon = (type) => {
        switch (type) {
            case 'merge_proposal':
                return '⟷';
            case 'merge_approved':
                return '✓';
            case 'merge_rejected':
                return '✕';
            case 'recipe_update_notice':
                return '📝';
            case 'test_notification':
                return '🧪';
            default:
                return '•';
        }
    };

    const getNotificationColor = (type) => {
        switch (type) {
            case 'merge_proposal':
                return 'text-blue-600';
            case 'merge_approved':
                return 'text-green-600';
            case 'merge_rejected':
                return 'text-red-600';
            case 'recipe_update_notice':
                return 'text-purple-600';
            case 'test_notification':
                return 'text-gray-600';
            default:
                return 'text-neutral-900';
        }
    };

    const handleCompareMerge = async (notification) => {
        if(!user) return;
        try{
            setProcessingMerge(true);
            const token = await user.getIdToken();
            const proposalId = notification.merge_proposal_id || notification.metadata?.merge_proposal_id;
            if(!proposalId){
                alert('Merge proposal id missing');
                return;
            }
            const proposal = await getMergeProposal(proposalId, token);
            setSelectedProposal(proposal);
            const [orig, upd] = await Promise.all([
                getRecipeById(proposal.original_recipe_id),
                getRecipeById(proposal.updated_recipe_id)
            ]);
            setOriginalRecipe(orig);
            setUpdatedRecipe(upd);
            setShowDiffModal(true);
        }catch(err){
            console.error('compare merge error', err);
            alert('Failed to load comparison');
        }finally{
            setProcessingMerge(false);
        }
    };

    const handleApproveMerge = async () => {
        if(!user || !selectedProposal) return;
        try{
            setProcessingMerge(true);
            const token = await user.getIdToken();
            await updateMergeProposal(selectedProposal.id,{status:'approved'},token);
            setShowDiffModal(false); setSelectedProposal(null); setOriginalRecipe(null); setUpdatedRecipe(null);
            fetchNotifications();
        }catch(err){console.error(err);}
        finally{setProcessingMerge(false);} };

    const handleOpenFriendRequest = (notification) => {
        setSelectedFriendRequest(notification);
        setShowFriendRequestModal(true);
    };

    const handleOpenNotificationModal = (notification) => {
        setSelectedNotification(notification);
        setShowNotificationModal(true);
    };

    const handleCloseNotificationModal = () => {
        setShowNotificationModal(false);
        setSelectedNotification(null);
    };

    const handleFriendRequestHandled = async (action) => {
        // Only mark as read and refresh if action was accept or reject (not cancel)
        if (action === 'accepted' || action === 'rejected') {
            if (selectedFriendRequest) {
                await handleMarkAsRead(selectedFriendRequest.id);
            }
            await fetchNotifications();
        }
        setShowFriendRequestModal(false);
        setSelectedFriendRequest(null);
    };

    const filteredNotifications = notifications.filter(notification => {
        if (filter === 'unread') return !notification.is_read;
        if (filter === 'read') return notification.is_read;
        return true;
    });

    return (
        <>
            <div className="min-h-screen bg-neutral-50">
                <div className="w-full px-0 py-8">
                    <div className="mb-1">
                        <Heading level={1} className="text-neutral-900" style={{ color: colorTokens.oxfordBlue }}>NOTIFICATIONS</Heading>
                    </div>
                    
                    {/* Header with filters and actions */}
                    <div className="bg-neutral-0 hairline px-2 py-2 sm:px-3 sm:py-2 md:px-4 md:py-2 mb-1">
                        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-4 sm:space-y-0">
                            <div className="flex items-center space-x-4">
                                <div className="flex space-x-2">
                                    <button
                                        onClick={() => setFilter('all')}
                                        className={`px-3 py-1 text-xs font-mono uppercase tracking-wider transition-colors ${
                                            filter === 'all' 
                                                ? 'bg-neutral-900 text-neutral-0' 
                                                : 'bg-neutral-50 text-neutral-900 hairline hover:bg-neutral-100'
                                        }`}
                                    >
                                        ALL
                                    </button>
                                    <button
                                        onClick={() => setFilter('unread')}
                                        className={`px-3 py-1 text-xs font-mono uppercase tracking-wider transition-colors ${
                                            filter === 'unread' 
                                                ? 'bg-neutral-900 text-neutral-0' 
                                                : 'bg-neutral-50 text-neutral-900 hairline hover:bg-neutral-100'
                                        }`}
                                    >
                                        UNREAD {unreadCount > 0 && `(${unreadCount})`}
                                    </button>
                                    <button
                                        onClick={() => setFilter('read')}
                                        className={`px-3 py-1 text-xs font-mono uppercase tracking-wider transition-colors ${
                                            filter === 'read' 
                                                ? 'bg-neutral-900 text-neutral-0' 
                                                : 'bg-neutral-50 text-neutral-900 hairline hover:bg-neutral-100'
                                        }`}
                                    >
                                        READ
                                    </button>
                                </div>
                            </div>
                            
                            {unreadCount > 0 && (
                                <button
                                    onClick={handleMarkAllAsRead}
                                    className="px-4 py-2 bg-neutral-50 text-neutral-900 hairline hover:bg-neutral-100 transition-colors font-mono text-sm uppercase tracking-wider"
                                >
                                    MARK ALL READ
                                </button>
                            )}
                        </div>
                    </div>
                </div>

                {/* Notifications list - Full width */}
                {loading ? (
                    <div className="bg-neutral-0 hairline minimal-padding text-center py-8">
                        <Text className="text-neutral-900 opacity-60">Loading notifications...</Text>
                    </div>
                ) : error ? (
                    <div className="bg-neutral-0 hairline minimal-padding text-center py-8">
                        <Text className="text-red-600">{error}</Text>
                    </div>
                ) : filteredNotifications.length === 0 ? (
                    <div className="bg-neutral-0 hairline minimal-padding text-center py-8">
                        <Text className="text-neutral-900 opacity-60">
                            {filter === 'unread' ? 'No unread notifications' : 
                             filter === 'read' ? 'No read notifications' : 
                             'No notifications yet'}
                        </Text>
                    </div>
                ) : (
                    <div className="space-y-2">
                        {filteredNotifications.map((notification) => (
                            <div
                                key={notification.id}
                                onClick={() => {
                                    if (notification.type === 'friend_request') {
                                        handleOpenFriendRequest(notification);
                                    } else if (notification.type === 'merge_proposal') {
                                        handleCompareMerge(notification);
                                    } else {
                                        handleOpenNotificationModal(notification);
                                    }
                                }}
                                className={`p-3 hairline transition-colors cursor-pointer ${notification.is_read ? 'bg-neutral-0':'bg-neutral-50'}`}
                            >
                                <div className="flex items-start space-x-3">
                                    {/* User Profile Picture */}
                                    <div className="w-10 h-10 bg-neutral-0 hairline flex items-center justify-center text-sm font-mono flex-shrink-0">
                                        {notification.sender_name ? notification.sender_name.charAt(0).toUpperCase() : '?'}
                                    </div>
                                    
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-start justify-between">
                                            <div className="flex-1">
                                                {/* User Name and Notification Info */}
                                                <div className="flex items-center space-x-2 mb-1">
                                                    <Text className="text-sm font-medium text-neutral-900">
                                                        {notification.sender_name || 'System'}
                                                    </Text>
                                                    <div className="flex items-center space-x-2">
                                                        <div className="text-lg">
                                                            {getNotificationIcon(notification.type)}
                                                        </div>
                                                        <Text className="text-sm font-medium text-neutral-900">
                                                            {notification.title}
                                                        </Text>
                                                    </div>
                                                    {!notification.is_read && (
                                                        <div className="w-2 h-2 bg-blue-600 rounded-full flex-shrink-0"></div>
                                                    )}
                                                </div>
                                                
                                                <div className="h-px bg-neutral-900 opacity-20 mb-2"></div>
                                                
                                                <Text className="text-xs text-neutral-900 opacity-80 mb-2 leading-relaxed">
                                                    {notification.message.length > 100 
                                                        ? `${notification.message.substring(0, 100)}...` 
                                                        : notification.message
                                                    }
                                                </Text>
                                                
                                                <div className="flex items-center space-x-3 text-xs text-neutral-900 opacity-60">
                                                    <span>{formatDate(notification.created_at)}</span>
                                                </div>
                                            </div>
                                            
                                            <div className="flex items-center space-x-1 ml-3">
                                                <button 
                                                    onClick={(e) => {
                                                        e.stopPropagation(); 
                                                        if (notification.type === 'friend_request') {
                                                            handleOpenFriendRequest(notification);
                                                        } else if (notification.type === 'merge_proposal') {
                                                            handleCompareMerge(notification);
                                                        } else {
                                                            handleOpenNotificationModal(notification);
                                                        }
                                                    }} 
                                                    className="w-6 h-6 bg-neutral-900 text-neutral-0 flex items-center justify-center text-xs font-mono hover:opacity-60 transition-opacity" 
                                                    title="View"
                                                >
                                                    →
                                                </button>
                                                <button 
                                                    onClick={(e) => {
                                                        e.stopPropagation(); 
                                                        handleDelete(notification.id);
                                                    }} 
                                                    className="w-6 h-6 bg-neutral-50 text-neutral-900 hairline hover:bg-neutral-900 hover:text-neutral-0 transition-colors text-xs font-mono" 
                                                    title="Delete"
                                                >
                                                    ✕
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
            
            {/* Modals */}
            {showDiffModal && (
                <RecipeDiffModal isOpen={showDiffModal} onClose={()=>{setShowDiffModal(false); setSelectedProposal(null);}} originalRecipe={originalRecipe||{}} updatedRecipe={updatedRecipe||{}} />
            )}
            
            {/* Friend Request Modal */}
            <FriendRequestModal
                isOpen={showFriendRequestModal}
                onClose={() => {
                    setShowFriendRequestModal(false);
                    setSelectedFriendRequest(null);
                }}
                notification={selectedFriendRequest}
                onRequestHandled={handleFriendRequestHandled}
            />

            {/* Notification Detail Modal */}
            {showNotificationModal && selectedNotification && (
                <Modal
                    isOpen={showNotificationModal}
                    onClose={handleCloseNotificationModal}
                    title={selectedNotification.title}
                    maxWidth="max-w-2xl"
                >
                    <div className="space-y-4">
                        <div className="flex items-start space-x-3">
                            <div className="text-2xl flex-shrink-0">
                                {getNotificationIcon(selectedNotification.type)}
                            </div>
                            <div className="flex-1">
                                <Text className="text-sm font-medium text-neutral-900 mb-2">
                                    {selectedNotification.title}
                                </Text>
                                <Text className="text-sm text-neutral-900 opacity-80 mb-3 leading-relaxed">
                                    {selectedNotification.message}
                                </Text>
                                <div className="flex items-center space-x-4 text-xs text-neutral-900 opacity-60 mb-4">
                                    <span>{formatDate(selectedNotification.created_at)}</span>
                                    {selectedNotification.sender_name && (
                                        <span>from {selectedNotification.sender_name}</span>
                                    )}
                                </div>
                            </div>
                        </div>
                        
                        <div className="h-px bg-neutral-900 opacity-20"></div>
                        
                        <div className="flex space-x-3">
                            {selectedNotification.type === 'merge_proposal' && (
                                <>
                                    <button 
                                        onClick={() => {
                                            handleCompareMerge(selectedNotification);
                                            handleCloseNotificationModal();
                                        }}
                                        className="px-4 py-2 bg-neutral-900 text-neutral-0 font-mono text-sm uppercase tracking-wider hover:opacity-60 transition-opacity"
                                    >
                                        VIEW CHANGES
                                    </button>
                                    <button 
                                        onClick={() => {
                                            handleApproveMerge();
                                            handleCloseNotificationModal();
                                        }}
                                        className="px-4 py-2 bg-neutral-50 text-neutral-900 hairline hover:bg-neutral-900 hover:text-neutral-0 transition-colors font-mono text-sm uppercase tracking-wider"
                                    >
                                        APPROVE
                                    </button>
                                    <button 
                                        onClick={() => {
                                            // Handle reject logic here
                                            handleCloseNotificationModal();
                                        }}
                                        className="px-4 py-2 bg-neutral-50 text-neutral-900 hairline hover:bg-neutral-900 hover:text-neutral-0 transition-colors font-mono text-sm uppercase tracking-wider"
                                    >
                                        REJECT
                                    </button>
                                </>
                            )}
                            
                            {selectedNotification.type === 'friend_request' && (
                                <>
                                    <button 
                                        onClick={() => {
                                            handleOpenFriendRequest(selectedNotification);
                                            handleCloseNotificationModal();
                                        }}
                                        className="px-4 py-2 bg-neutral-900 text-neutral-0 font-mono text-sm uppercase tracking-wider hover:opacity-60 transition-opacity"
                                    >
                                        RESPOND
                                    </button>
                                </>
                            )}
                            
                            {!selectedNotification.is_read && (
                                <button 
                                    onClick={() => {
                                        handleMarkAsRead(selectedNotification.id);
                                        handleCloseNotificationModal();
                                    }}
                                    className="px-4 py-2 bg-neutral-50 text-neutral-900 hairline hover:bg-neutral-900 hover:text-neutral-0 transition-colors font-mono text-sm uppercase tracking-wider"
                                >
                                    MARK READ
                                </button>
                            )}
                            
                            <button 
                                onClick={() => {
                                    handleDelete(selectedNotification.id);
                                    handleCloseNotificationModal();
                                }}
                                className="px-4 py-2 bg-neutral-50 text-neutral-900 hairline hover:bg-neutral-900 hover:text-neutral-0 transition-colors font-mono text-sm uppercase tracking-wider"
                            >
                                DELETE
                            </button>
                        </div>
                    </div>
                </Modal>
            )}
        </>
    );
};

export default NotificationsPage;
